import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import type { DevicePairing, DevicePairRequest, MarketplaceFee, Settings as SettingsDto, Workspace, WorkspaceUpdate } from "@sable/api-contract";
import { ErrorBlock, Field, Loading, MoneyInput } from "../components/ui.tsx";
import { t, tk } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { extensionPresent, pairExtension, pairingCode } from "../lib/extension.ts";
import { shortDate } from "../lib/format.ts";
import { keys, useDevices, useFees, useMarketplaces, useMe, useMembers, useSettings } from "../lib/queries.ts";
import { timeZones } from "../lib/regions.ts";
import { signOut } from "../lib/session.ts";
import { useCan, useWs, wsPath } from "../lib/workspace.tsx";
import { SmsCard } from "../components/SmsCard.tsx";

function SaveBar({ dirty, busy, saved, error, onSave }: { dirty: boolean; busy: boolean; saved: boolean; error: unknown; onSave: () => void }) {
  return (
    <div className="row">
      <button className="btn btn-primary btn-sm" disabled={!dirty || busy} onClick={onSave}>{busy ? t("app.saving") : t("app.save")}</button>
      {saved && !dirty && <span className="muted small">{t("app.saved")}</span>}
      {error instanceof Error && <span className="form-error">{error.message}</span>}
    </div>
  );
}

function WorkspaceCard() {
  const ws = useWs();
  const qc = useQueryClient();
  const can = useCan("workspace.manage");
  const [form, setForm] = useState({ name: ws.name, timezone: ws.timezone, units: ws.units, skuPrefix: ws.skuPrefix, personalItems: ws.personalItems });
  const [state, setState] = useState<{ busy: boolean; saved: boolean; error: unknown }>({ busy: false, saved: false, error: null });
  const dirty = JSON.stringify(form) !== JSON.stringify({ name: ws.name, timezone: ws.timezone, units: ws.units, skuPrefix: ws.skuPrefix, personalItems: ws.personalItems });

  async function save() {
    setState({ busy: true, saved: false, error: null });
    try {
      const body: WorkspaceUpdate = form;
      const next = await request<Workspace>("/workspace", { method: "PATCH", workspace: ws.id, body });
      qc.setQueryData(keys.workspace(ws.id), next);
      void qc.invalidateQueries({ queryKey: keys.me });
      setState({ busy: false, saved: true, error: null });
    } catch (error) {
      setState({ busy: false, saved: false, error });
    }
  }

  return (
    <section className="card stack">
      <h2>{t("settings.workspace")}</h2>
      <fieldset disabled={!can} className="stack plain">
        <div className="grid-2">
          <Field label={t("settings.name")}><input className="input" value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label={t("settings.timezone")}>
            <select className="select" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
              {timeZones().map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label={t("settings.units")}>
            <select className="select" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value as Workspace["units"] })}>
              <option value="imperial">{t("onboarding.imperial")}</option>
              <option value="metric">{t("onboarding.metric")}</option>
            </select>
          </Field>
          <Field label={t("settings.skuPrefix")} hint={t("settings.skuPrefixHint")}>
            <input className="input mono" value={form.skuPrefix} maxLength={8} pattern="[A-Z0-9-]{0,8}"
              onChange={(e) => setForm({ ...form, skuPrefix: e.target.value.toUpperCase() })} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.personalItems} onChange={(e) => setForm({ ...form, personalItems: e.target.checked })} />
          {t("settings.personalItems")}
        </label>
      </fieldset>
      {can && <SaveBar dirty={dirty} {...state} onSave={() => void save()} />}
    </section>
  );
}

function EconomicsCard() {
  const ws = useWs();
  const qc = useQueryClient();
  const can = useCan("settings.write");
  const q = useSettings(ws.id);
  const [form, setForm] = useState<SettingsDto | null>(null);
  const [state, setState] = useState<{ busy: boolean; saved: boolean; error: unknown }>({ busy: false, saved: false, error: null });
  useEffect(() => { if (q.data && !form) setForm(q.data); }, [q.data, form]);
  if (q.isPending || !form) return <section className="card"><Loading /></section>;
  if (q.isError) return <ErrorBlock error={q.error} />;
  const dirty = JSON.stringify(form) !== JSON.stringify(q.data);
  const c = ws.homeCurrency;
  const money = (k: keyof SettingsDto, label: string) => (
    <Field label={label}>
      <MoneyInput currency={c} locale={ws.locale} value={form[k] as number} onChange={(v) => setForm({ ...form, [k]: v ?? 0 })} />
    </Field>
  );

  async function save() {
    setState({ busy: true, saved: false, error: null });
    try {
      const next = await request<SettingsDto>("/settings", { method: "PATCH", workspace: ws.id, body: form });
      qc.setQueryData(keys.settings(ws.id), next);
      setForm(next);
      setState({ busy: false, saved: true, error: null });
    } catch (error) {
      setState({ busy: false, saved: false, error });
    }
  }

  return (
    <section className="card stack">
      <div>
        <h2>{t("settings.economics")}</h2>
        <p className="muted small">{t("settings.economicsHint")}</p>
      </div>
      <fieldset disabled={!can} className="stack plain">
        <div className="grid-2">
          {money("polyMailerCostMinor", t("settings.polyMailer"))}
          {money("bubbleMailerCostMinor", t("settings.bubbleMailer"))}
          {money("avgPackagingCostMinor", t("settings.avgPackaging"))}
          {money("avgShippingCostMinor", t("settings.avgShipping"))}
          {money("minAcceptableProfitMinor", t("settings.minProfit"))}
          <Field label={t("settings.targetMargin")}>
            <input className="input num" type="number" min={0} max={100} step="0.5" value={form.targetMarginPct}
              onChange={(e) => setForm({ ...form, targetMarginPct: Number(e.target.value) })} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.buyerPaysShipping} onChange={(e) => setForm({ ...form, buyerPaysShipping: e.target.checked })} />
          {t("settings.buyerPaysShipping")}
        </label>
      </fieldset>
      {can && <SaveBar dirty={dirty} {...state} onSave={() => void save()} />}
    </section>
  );
}

function FeeRow({ fee, name }: { fee: MarketplaceFee; name: string }) {
  const ws = useWs();
  const qc = useQueryClient();
  const can = useCan("settings.write");
  const [form, setForm] = useState(fee);
  const [state, setState] = useState<{ busy: boolean; saved: boolean; error: unknown }>({ busy: false, saved: false, error: null });
  const dirty = JSON.stringify(form) !== JSON.stringify(fee);
  const c = ws.homeCurrency;
  const pct = (k: "commissionPct" | "paymentPct" | "boostPct", label: string) => (
    <Field label={label}>
      <input className="input num" type="number" min={0} max={100} step="0.01" value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} />
    </Field>
  );

  async function save() {
    setState({ busy: true, saved: false, error: null });
    try {
      const { marketplace, ...body } = form;
      const next = await request<MarketplaceFee>(`/fees/${marketplace}`, { method: "PATCH", workspace: ws.id, body });
      qc.setQueryData<MarketplaceFee[]>(keys.fees(ws.id), (old) => old?.map((f) => (f.marketplace === next.marketplace ? next : f)));
      setState({ busy: false, saved: true, error: null });
    } catch (error) {
      setState({ busy: false, saved: false, error });
    }
  }

  return (
    <div className="fee-row">
      <div className="spread"><h3>{name}</h3>{can && <SaveBar dirty={dirty} {...state} onSave={() => void save()} />}</div>
      <fieldset disabled={!can} className="plain">
        <div className="grid-3">
          {pct("commissionPct", t("settings.commission"))}
          <Field label={t("settings.fixedFee")}><MoneyInput currency={c} locale={ws.locale} value={form.fixedFeeMinor} onChange={(v) => setForm({ ...form, fixedFeeMinor: v ?? 0 })} /></Field>
          {pct("paymentPct", t("settings.paymentPct"))}
          <Field label={t("settings.paymentFixed")}><MoneyInput currency={c} locale={ws.locale} value={form.paymentFixedMinor} onChange={(v) => setForm({ ...form, paymentFixedMinor: v ?? 0 })} /></Field>
          <Field label={t("settings.lowThreshold")}><MoneyInput currency={c} locale={ws.locale} value={form.lowPriceThresholdMinor} onChange={(v) => setForm({ ...form, lowPriceThresholdMinor: v })} /></Field>
          <Field label={t("settings.lowFee")}><MoneyInput currency={c} locale={ws.locale} value={form.lowPriceFeeMinor} onChange={(v) => setForm({ ...form, lowPriceFeeMinor: v })} /></Field>
          {pct("boostPct", t("settings.boost"))}
        </div>
      </fieldset>
    </div>
  );
}

function FeesCard() {
  const ws = useWs();
  const fees = useFees(ws.id);
  const mps = useMarketplaces();
  return (
    <section className="card stack">
      <div>
        <h2>{t("settings.fees")}</h2>
        <p className="muted small">{t("settings.feesHint")}</p>
      </div>
      {fees.isPending ? <Loading /> : fees.isError ? <ErrorBlock error={fees.error} /> : (
        <div className="fee-grid">
          {fees.data.map((f) => <FeeRow key={f.marketplace} fee={f} name={mps.data?.find((m) => m.code === f.marketplace)?.name ?? f.marketplace} />)}
        </div>
      )}
    </section>
  );
}

function ExtensionCard() {
  const ws = useWs();
  const qc = useQueryClient();
  const can = useCan("listings.write");
  const devices = useDevices(ws.id);
  const mps = useMarketplaces();
  const [name, setName] = useState("");
  const [state, setState] = useState<{ busy: boolean; done: boolean; code: string | null; error: unknown }>({ busy: false, done: false, code: null, error: null });
  const mpName = (code: string) => mps.data?.find((m) => m.code === code)?.name ?? code;

  async function pair() {
    setState({ busy: true, done: false, code: null, error: null });
    try {
      const present = await extensionPresent();
      const body: DevicePairRequest = { name: name.trim() };
      const { token } = await request<DevicePairing>("/extension/devices", { method: "POST", workspace: ws.id, body });
      if (!present) {
        setState({ busy: false, done: false, code: pairingCode(token), error: null });
      } else {
        const res = await pairExtension(token, body.name);
        setState(res.ok ? { busy: false, done: true, code: null, error: null } : { busy: false, done: false, code: pairingCode(token), error: new Error(res.error ?? "") });
      }
      setName("");
      void qc.invalidateQueries({ queryKey: keys.devices(ws.id) });
    } catch (error) {
      setState({ busy: false, done: false, code: null, error });
    }
  }

  async function unpair(id: string, deviceName: string) {
    if (!confirm(t("extension.unpairConfirm", { name: deviceName }))) return;
    await request(`/extension/devices/${id}`, { method: "DELETE", workspace: ws.id });
    void qc.invalidateQueries({ queryKey: keys.devices(ws.id) });
  }

  return (
    <section className="card stack" id="extension">
      <div>
        <h2>{t("extension.title")}</h2>
        <p className="muted small">{t("extension.hint")}</p>
        {can && <p className="small"><Link to={wsPath(ws, "/shops")}>{t("extension.readShops")}</Link></p>}
      </div>
      {devices.isPending ? <Loading /> : devices.isError ? <ErrorBlock error={devices.error} /> : devices.data.length === 0 ? (
        <p className="muted small">{t("extension.none")}</p>
      ) : (
        <div className="list">
          {devices.data.map((d) => {
            const sites = Object.entries(d.signedIn).filter(([, on]) => on).map(([code]) => {
              const handle = d.accounts?.[code]?.handle;
              return handle ? `${mpName(code)} (@${handle})` : mpName(code);
            });
            return (
              <div className="list-row" key={d.id}>
                <div className="grow">
                  <div className="name">{d.name}{d.version ? <span className="muted small"> · v{d.version}</span> : null}</div>
                  <div className="sub">
                    {d.online ? t("extension.online") : d.lastSeenAt ? t("extension.offline", { when: shortDate(d.lastSeenAt) }) : t("extension.never")}
                    {d.paused ? ` · ${t("extension.paused")}` : ""}
                    {" · "}
                    {sites.length ? t("extension.signedInTo", { sites: sites.join(", ") }) : t("extension.signedInNone")}
                    {d.pairedBy ? ` · ${t("extension.pairedBy", { email: d.pairedBy })}` : ""}
                  </div>
                </div>
                <span className={`pill ${d.online ? "pill-active" : "pill-ended"}`}>{d.online ? t("extension.online") : t("extension.offlineShort")}</span>
                {can && <button className="btn btn-sm btn-ghost" onClick={() => void unpair(d.id, d.name)}>{t("extension.unpair")}</button>}
              </div>
            );
          })}
        </div>
      )}
      {can && (
        <div className="stack">
          <div className="row-wrap">
            <input className="input grow" value={name} maxLength={80} placeholder={t("extension.namePlaceholder")} aria-label={t("extension.name")}
              onChange={(e) => setName(e.target.value)} />
            <button className="btn btn-primary btn-sm" disabled={state.busy || !name.trim()} onClick={() => void pair()}>
              {state.busy ? t("extension.pairing") : t("extension.pair")}
            </button>
          </div>
          {state.done && <div className="banner banner-ok">{t("extension.paired")}</div>}
          {state.code && (
            <div className="banner stack">
              <span>{t("extension.notDetected")}</span>
              <code className="xl-code">{state.code}</code>
              <span className="small">{t("extension.codeOnce")}</span>
            </div>
          )}
          {state.error instanceof Error && state.error.message && <p className="form-error">{state.error.message}</p>}
        </div>
      )}
    </section>
  );
}

function PeopleCard() {
  const ws = useWs();
  const members = useMembers(ws.id);
  return (
    <section className="card">
      <h2>{t("settings.members")}</h2>
      {members.isPending ? <Loading /> : members.isError ? <ErrorBlock error={members.error} /> : (
        <div className="list">
          {members.data.map((m) => (
            <div className="list-row" key={m.userId}>
              <div className="grow"><div className="name">{m.displayName ?? m.email}</div>{m.displayName && <div className="sub">{m.email}</div>}</div>
              <span className="pill pill-plain">{tk("settings.role", m.role)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function Settings() {
  const me = useMe();
  const finance = useCan("finance.read");
  return (
    <div className="stack-lg">
      <div className="page-head"><h1>{t("settings.title")}</h1></div>
      <WorkspaceCard />
      {finance && <EconomicsCard />}
      {finance && <FeesCard />}
      <SmsCard />
      <ExtensionCard />
      <PeopleCard />
      <section className="card spread">
        <div>
          <h2>{t("settings.account")}</h2>
          {me.data && <p className="muted small">{t("settings.signedInAs", { email: me.data.user.email })}</p>}
        </div>
        <button className="btn" onClick={() => void signOut()}>{t("nav.signOut")}</button>
      </section>
    </div>
  );
}
